import classNames from 'classnames';
import type { Bounds, Range } from 'quill';
import Quill from 'quill';

import { useEffect, useMemo, useState } from 'react';
import { useAsync } from 'react-async-hook';
import type { Root } from 'react-dom/client';
import { createRoot } from 'react-dom/client';
import { getNext, getPrevious } from '../../../../services/List';
import Button from '../../../Button';
import { parseContent } from './parser';
import styles from './style.m.less';

const Parchment = Quill.import('parchment');

const asPx = (str: string | number) => `${str}px`;

export function ResultsContainer({
    children,
    style,
    className,
}: {
    children: React.ReactNode;
    style?: React.CSSProperties;
    className?: string;
}) {
    return (
        <div className={classNames(styles.container, className)} style={style}>
            {children}
        </div>
    );
}

export function ResultsItem({
    onClick,
    onHighlight,
    isHighlighted,
    children,
    className,
    style,
}: {
    onClick?: () => void;
    onHighlight?: () => void;
    isHighlighted?: boolean;
    children: React.ReactNode;
    style?: React.CSSProperties;
    className?: string;
}) {
    const cN = classNames(styles.item, { [styles.highlighted]: isHighlighted });
    return (
        <Button
            kind="PLAIN"
            onClick={onClick}
            onMouseOver={onHighlight}
            className={classNames(cN, className)}
            style={style}
        >
            {children}
        </Button>
    );
}

export function ResultsMoreItems({ count, children }: { count: number; children: React.ReactNode }) {
    const body = children || `and ${count} more`;
    return <div className={styles.moreItems}>{body}</div>;
}

type Query = {
    string: string;
    start: number;
    end: number;
    index: number;
    length: number;
    trigger?: unknown;
};

type AutocompleteWrapperProps<T> = {
    eventNode: HTMLElement;
    query: Query;
    wrapperComponent: React.ComponentType<{ children: React.ReactNode }>;
    itemComponent: React.ComponentType<{
        item: T;
        isHighlighted: boolean;
        onClick: () => void;
        onHighlight: () => void;
    }>;
    moreItemsComponent?: React.ComponentType<{ count: number }>;
    maxItemsShown?: number;
    itemToKey: (item: T) => string | number;
    onSearch: (query: Query) => Promise<T[]>;
    onSelect: (item: T, query: Query) => void;
};

function AutocompleteWrapper<T>({
    eventNode,
    query,
    onSearch,
    onSelect,
    maxItemsShown,
    moreItemsComponent: MoreItems,
    itemToKey,
    wrapperComponent: Wrapper,
    itemComponent: Item,
}: AutocompleteWrapperProps<T>) {
    const { result: fetchedItems } = useAsync(
        () => (query ? onSearch(query) : Promise.resolve(undefined)),
        [onSearch, query]
    );
    const items = useMemo(() => fetchedItems ?? [], [fetchedItems]);
    const shownItems = useMemo(() => items.slice(0, maxItemsShown || Infinity), [items, maxItemsShown]);
    const [highlightedItem, setHighlightedItem] = useHighlightedItem(eventNode, shownItems);
    useKeyboardSelect(eventNode, onSelect, query, highlightedItem);

    if (!shownItems.length) {
        return null;
    }

    const remainingCount = items.length - shownItems.length;
    const canShowMoreItems = MoreItems && remainingCount > 0;

    return (
        <Wrapper>
            <div>
                {shownItems.map(item => (
                    <Item
                        key={itemToKey(item)}
                        item={item}
                        isHighlighted={item === highlightedItem}
                        onClick={() => onSelect(item, query)}
                        onHighlight={() => setHighlightedItem(item)}
                    />
                ))}
                {canShowMoreItems && <MoreItems count={remainingCount} />}
            </div>
        </Wrapper>
    );
}

type AutocompleteInstance = {
    show: (pos: Bounds) => void;
    hide: () => void;
    isShown: () => boolean;
    search: (query: Query) => void;
};

const checkForTriggers = (quill: Quill, instance: AutocompleteInstance, selection: Range | null, triggers: unknown) => {
    if (!selection) {
        return instance.hide();
    }

    const { length, index } = selection;
    if (length) {
        return instance.hide();
    }

    const [leaf, offset] = quill.getLeaf(index);
    if (!(leaf instanceof Parchment.TextBlot)) {
        return;
    }

    const leafResult = parseContent(leaf.value(), offset, triggers);

    if (!leafResult) {
        return instance.hide();
    }

    // As we searched for our trigger only within the space of our current Leaf blot,
    // we need to calibrate the real start and end positions of our trigger in respect
    // to our current index.

    // Check how much the cursor moved forward inside the leaf and subtract this value from the index
    const start = index - (offset - leafResult.start);
    // Move the end by the same amount as the start moved
    const end = leafResult.end + start - leafResult.start;
    const result = {
        ...leafResult,
        start,
        end,
        index: start,
        length: end - start,
    };

    if (!instance.isShown()) {
        instance.show(quill.getBounds(result.start)!);
    }

    instance.search(result);
};

const listen = (quill: Quill, instance: AutocompleteInstance, triggers: unknown[]) => {
    quill.on('text-change', () => checkForTriggers(quill, instance, quill.getSelection(), triggers));
    quill.on('selection-change', sel => {
        // here we check if the user just moved away from
        // the completion menu!
        if (instance.isShown() && sel) {
            checkForTriggers(quill, instance, sel, triggers);
        }

        // onBlur
        if (!sel) {
            // this blur might be triggered from within a completion element we're currently
            // using. Give its handler a chance to perform an action, and only blur if
            // it didn't reestablish focus
            setTimeout(() => {
                if (!quill.getSelection()) {
                    instance.hide();
                }
            });
        }
    });
};

export const replaceQueryWithEmbed = (quill: Quill, query: Query, embedType: string, embedValue: unknown) => {
    const { index, length } = query;

    quill.deleteText(index, length);
    quill.insertEmbed(index, embedType, embedValue);
    setTimeout(() => {
        quill.insertText(index + 1, ' ');
        quill.setSelection(index + 2);
    });
};

export const replaceQueryWithText = (quill: Quill, query: Query, text: string) => {
    const { index, length } = query;

    quill.deleteText(index, length);
    quill.insertText(index, text);
    setTimeout(() => {
        quill.insertText(index + text.length, ' ');
        quill.setSelection(index + text.length + 1);
    });
};

export const replaceQueryWithEmoji = (quill: Quill, query: Query, emojiText: string) => {
    const { index, length } = query;

    quill.deleteText(index, length);
    quill.insertText(index, emojiText);
    setTimeout(() => {
        quill.setSelection(index + emojiText.length + 1);
    });
};

type CreateInstanceProps<T> = Pick<
    AutocompleteWrapperProps<T>,
    | 'wrapperComponent'
    | 'itemComponent'
    | 'moreItemsComponent'
    | 'maxItemsShown'
    | 'itemToKey'
    | 'onSearch'
    | 'onSelect'
> & { quill: Quill; container: HTMLElement };

export function createInstance<T>({ quill, container, ...props }: CreateInstanceProps<T>) {
    let reactRoot: Root | null = null;

    const getRoot = () => {
        if (!reactRoot) {
            reactRoot = createRoot(container);
        }
        return reactRoot;
    };

    const render = (query: Query) => {
        getRoot().render(<AutocompleteWrapper {...props} eventNode={quill.container} query={query} />);
    };

    const unmount = () => {
        if (!reactRoot) {
            return;
        }
        reactRoot.unmount();
        reactRoot = null;
    };

    const hide = () => {
        container.style.display = 'none';
        unmount();
    };

    const escHandler = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            hide();
            quill.container.removeEventListener('keydown', escHandler);
        }
    };

    const show = (position: Bounds) => {
        container.style.display = 'block';
        container.style.position = 'absolute';
        container.style.zIndex = '100';
        container.style.top = asPx(position.top + position.height);

        const quillPosition = (container.parentNode! as HTMLElement).getBoundingClientRect();
        const autocompleteWidth = 220; /* min-width of autocomplete */
        if (position.left + autocompleteWidth > quillPosition.width) {
            container.style.left = asPx(quillPosition.width - autocompleteWidth);
        } else {
            container.style.left = asPx(position.left);
        }

        quill.container.addEventListener('keydown', escHandler);
    };

    const search = (query: Query) => render(query);

    const isShown = () => !!reactRoot;

    const instance = { show, hide, search, isShown };

    const check = (selection: Range, triggers: unknown[]) => checkForTriggers(quill, instance, selection, triggers);
    const checkDeferred = (selection: Range, triggers: unknown[]) => setTimeout(() => check(selection, triggers), 10);

    const openAtIndex = (index: number) => {
        show(quill.getBounds(index)!);
        render({
            index,
            start: index,
            end: index,
            length: 0,
            string: '',
        });
    };

    return {
        check,
        checkDeferred,
        listen: (triggers: unknown[]) => listen(quill, instance, triggers),
        hide,
        openAtIndex,
    };
}

function useHighlightedItem<T>(eventNode: HTMLElement, items?: T[]) {
    const [highlightedItem, setHighlightedItem] = useState<T>();

    useEffect(() => {
        if (!items || !items.length) {
            return;
        }

        const firstItem = items[0];
        setHighlightedItem(firstItem);

        const getMoveFn = (key: string) => {
            switch (key) {
                case 'ArrowDown':
                    return getNext;
                case 'ArrowUp':
                    return getPrevious;
            }
        };

        const handler = (event: KeyboardEvent) => {
            const moveFn = getMoveFn(event.key);

            if (!moveFn) {
                return;
            }

            event.preventDefault();
            setHighlightedItem(item => moveFn(items, item) ?? item);
        };

        eventNode.addEventListener('keydown', handler);

        return () => eventNode.removeEventListener('keydown', handler);
    }, [eventNode, items]);

    return [highlightedItem, setHighlightedItem] as const;
}

function useKeyboardSelect<T>(
    eventNode: HTMLElement,
    onSelect: (item: T, query: Query) => void,
    query: Query,
    highlightedItem?: T
) {
    useEffect(() => {
        const handler = (event: KeyboardEvent) => {
            if (highlightedItem && event.key === 'Enter') {
                event.stopPropagation();
                event.preventDefault();
                onSelect(highlightedItem, query);
            }
        };
        eventNode.addEventListener('keydown', handler, true);
        return () => eventNode.removeEventListener('keydown', handler, true);
    }, [eventNode, highlightedItem, onSelect, query]);
}
